import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TextFieldMUI, { TextFieldProps as TextFieldPropsMUI } from '@material-ui/core/TextField';

import { defaultTheme, WithMaterialThemeProps } from '../../styles';
import Spinner from '../spinner';
import { Root } from './styled';

const {
	forwardRef,
	useRef,
	useImperativeHandle,
	useCallback,
} = React;


export type InputStandartMargins = 'bottom-none' | 'bottom-small' | 'bottom-medium';

export type TextFieldProps = {
	value: string;
	readonly?: boolean;
	renderHelperText?: () => React.ReactNode;
	margin?: InputStandartMargins;
	isFetching?: boolean;
	onChange?: (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, value: string) => void;
	onKeyDown?: React.EventHandler<any>;
	onBlur?: React.EventHandler<any>;
	onFocus?: React.EventHandler<any>;
	onClick?: React.EventHandler<any>;
} & Pick<TextFieldPropsMUI,
	'autoComplete'
	| 'autoFocus'
	| 'disabled'
	| 'error'
	| 'fullWidth'
	| 'helperText'
	| 'name'
	| 'id'
	| 'inputRef'
	| 'label'
	| 'multiline'
	| 'placeholder'
	| 'required'
	| 'rows'
	| 'rowsMax'
	| 'type'
	| 'InputProps'
	| 'inputProps'
	| 'error'>

type Ref = {
	input: HTMLInputElement
}

const useInputStyles = makeStyles<any, TextFieldProps, any>((materialTheme: WithMaterialThemeProps) => {
	const { customTheme } = materialTheme;
	const theme = customTheme || defaultTheme; 

	return {
		underline: ({ readonly, error }) => ({
			'&:before': {
				borderBottomWidth: 1,
				borderBottomColor: !error && theme.color.blackDisabled,
			},
			'&:hover:not(.Mui-disabled):before': {
				borderBottomWidth: 1,
				borderBottomColor: readonly ? theme.color.blackDisabled : 'rgba(0, 0, 0, 0.42)',
			},
			'&:after': {
				borderColor: theme.color.accentPrimary,
				borderBottom: readonly && 'none'
			}
		})
	}
});

const useLabelStyles = makeStyles<any, TextFieldProps, any>((materialTheme: WithMaterialThemeProps) => {
	const { customTheme } = materialTheme;
	const theme = customTheme || defaultTheme; 

	return {
		root: ({ error, readonly }) => {	
			return {
				'&': {
					color: readonly ? theme.color.blackPrimary : theme.color.blackSecondary,
				},
				'&.Mui-focused': {
					color: readonly
						? theme.color.blackPrimary
						: !error
							? theme.color.accentPrimary
							: theme.color.dangerPrimary
				},
			}	
		}
	}
});

const TextField = forwardRef<Ref, TextFieldProps>((props, ref) => {
	const {
		name,
		value,
		autoFocus,
		margin,
		isFetching,
		error,
		required,
		InputProps,
		helperText,
		renderHelperText,
		fullWidth,
		readonly,
		disabled,
		onChange,
		...rest
	} = props;
	const inputRef = useRef(null);
	const inputClasses = useInputStyles(props);
	const labelClasses = useLabelStyles(props);
	const endAdornment = isFetching
		? <Spinner appearance='embedded' />
		: InputProps && InputProps.endAdornment;
	const renderedHelperText = typeof renderHelperText === 'function'
		? renderHelperText()
		: helperText;

	const handleChange = useCallback((e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		typeof onChange === 'function' && onChange(e, e.currentTarget.value);
	}, [onChange]);

	useImperativeHandle(ref, () => ({ input: inputRef.current.querySelector('input') }));

	return (
		<Root
			margin={margin}
			isFetching={isFetching}
			fullWidth={fullWidth}>
			<TextFieldMUI
				ref={inputRef}
				{...rest}
				variant='standard'
				value={value}
				error={error}
				autoFocus={autoFocus}
				helperText={renderedHelperText}
				disabled={disabled || isFetching}
				InputProps={{
					...InputProps,
					endAdornment,
					name,
					readOnly: Boolean(readonly),
					classes: inputClasses,
				}}
				InputLabelProps={{
					shrink: true,
					classes: labelClasses,
        }}
				required={required}
				fullWidth={fullWidth}
				onChange={handleChange}
			/>
		</Root>
	);
});

TextField.displayName = 'TextField';
TextField.defaultProps = {
	margin: 'bottom-small',
};

export default TextField;
